import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LCSubPageLayout from './LCSubPageLayout';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getChapterBylaws,
  createOrUpdateChapterBylaws,
  deleteChapterBylaws,
  type ChapterByLawsType,
} from '../../../endpoints/chaptersTs';
import type { ByLawsResponse, ByLawsSection } from '../../../types/chapters';
import { BylawsEditor } from './BylawsEditor';
import { LIST_CONTENT_TYPE_ID } from './bylawsEditorConstants';

const DOCUMENT_LABELS: Record<
  ChapterByLawsType,
  {
    title: string;
    loading: string;
    empty: string;
    add: string;
    deleteButton: string;
    deleteTitle: string;
    deleteBody: string;
  }
> = {
  by_laws: {
    title: 'Bylaws',
    loading: 'Loading bylaws...',
    empty: 'No bylaws have been added for this chapter.',
    add: 'Add Bylaws',
    deleteButton: 'Delete Bylaws',
    deleteTitle: 'Delete chapter bylaws?',
    deleteBody:
      'This will remove all bylaws content for this chapter. This action cannot be undone.',
  },
  standing_rules: {
    title: 'Standing Rules',
    loading: 'Loading standing rules...',
    empty: 'No standing rules have been added for this chapter.',
    add: 'Add Standing Rules',
    deleteButton: 'Delete Standing Rules',
    deleteTitle: 'Delete chapter standing rules?',
    deleteBody:
      'This will remove all standing rules content for this chapter. This action cannot be undone.',
  },
};

interface LCBylawsPageProps {
  documentType?: ChapterByLawsType;
}

const LCBylawsPage: React.FC<LCBylawsPageProps> = ({
  documentType = 'by_laws',
}) => {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const labels = DOCUMENT_LABELS[documentType];

  return (
    <LCSubPageLayout title={labels.title}>
      {(detail) => (
        <LCBylawsContent
          chapterId={detail.id}
          documentType={documentType}
          canEditBylaws={detail.can_edit_bylaws ?? false}
          deleteConfirmOpen={deleteConfirmOpen}
          setDeleteConfirmOpen={setDeleteConfirmOpen}
          isDeleting={isDeleting}
          setIsDeleting={setIsDeleting}
        />
      )}
    </LCSubPageLayout>
  );
};

interface LCBylawsContentProps {
  chapterId: string;
  documentType: ChapterByLawsType;
  canEditBylaws: boolean;
  deleteConfirmOpen: boolean;
  setDeleteConfirmOpen: (v: boolean) => void;
  isDeleting: boolean;
  setIsDeleting: (v: boolean) => void;
}

function formatLastUpdatedAt(value?: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function LastUpdatedLine({ bylaws }: { bylaws?: ByLawsResponse | null }) {
  if (!bylaws?.id || !bylaws.updated_at) return null;
  const when = formatLastUpdatedAt(bylaws.updated_at);
  if (!when) return null;
  const who = bylaws.updated_by_name?.trim() || 'Unknown';
  return (
    <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
      Last updated {when} by {who}
    </Typography>
  );
}

function EditActions({
  onSave,
  onCancel,
  isSaving,
}: {
  onSave: () => void;
  onCancel: () => void;
  isSaving: boolean;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button variant="contained" onClick={onSave} disabled={isSaving}>
        Save
      </Button>
      <Button variant="outlined" onClick={onCancel} disabled={isSaving}>
        Cancel
      </Button>
    </Box>
  );
}

const LCBylawsContent: React.FC<LCBylawsContentProps> = ({
  chapterId,
  documentType,
  canEditBylaws,
  deleteConfirmOpen,
  setDeleteConfirmOpen,
  isDeleting,
  setIsDeleting,
}) => {
  const labels = DOCUMENT_LABELS[documentType];
  const queryKey = ['chapter-bylaws', chapterId, documentType];

  const {
    data: bylaws,
    isLoading,
    refetch,
  } = useApiGet(queryKey, () => getChapterBylaws(chapterId, documentType), {
    enabled: !!chapterId,
  });

  const { mutate: saveBylaws, isPending: isSaving } = useApiSend(
    () => createOrUpdateChapterBylaws(chapterId, bylawsPayload, documentType),
    () => {
      refetch();
      setEditMode(false);
    },
    undefined,
    [queryKey]
  );

  const [editMode, setEditMode] = useState(false);
  const [bylawsPayload, setBylawsPayload] = useState<ByLawsResponse>({
    sections: [],
  });

  const isEmpty = !bylaws?.id || !bylaws.sections?.length;

  const handleCreate = () => {
    setBylawsPayload({ id: undefined, sections: [] });
    setEditMode(true);
  };

  const handleEdit = () => {
    setBylawsPayload({
      id: bylaws?.id ?? undefined,
      sections: bylaws?.sections ?? [],
    });
    setEditMode(true);
  };

  const handleSave = () => {
    saveBylaws(undefined as any);
  };

  const handleDeleteClick = () => setDeleteConfirmOpen(true);
  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await deleteChapterBylaws(chapterId, documentType);
      refetch();
      setDeleteConfirmOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  if (isLoading) {
    return <Typography color="text.secondary">{labels.loading}</Typography>;
  }

  if (editMode) {
    return (
      <Box sx={{ mt: 2 }}>
        <LastUpdatedLine bylaws={bylaws} />
        <Box sx={{ mb: 2 }}>
          <EditActions
            onSave={handleSave}
            onCancel={() => setEditMode(false)}
            isSaving={isSaving}
          />
        </Box>
        <BylawsEditor payload={bylawsPayload} onChange={setBylawsPayload} />
        <Box sx={{ mt: 2 }}>
          <EditActions
            onSave={handleSave}
            onCancel={() => setEditMode(false)}
            isSaving={isSaving}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      {isEmpty ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography color="text.secondary" gutterBottom>
            {labels.empty}
          </Typography>
          {canEditBylaws && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
              sx={{ mt: 2 }}
            >
              {labels.add}
            </Button>
          )}
        </Paper>
      ) : (
        <>
          <LastUpdatedLine bylaws={bylaws} />
          {canEditBylaws && (
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                onClick={handleEdit}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteClick}
              >
                {labels.deleteButton}
              </Button>
            </Box>
          )}
          <BylawsView sections={bylaws?.sections ?? []} />
        </>
      )}

      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>{labels.deleteTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText>{labels.deleteBody}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

function BylawsView({ sections }: { sections: ByLawsSection[] }) {
  if (!sections?.length) return null;

  return (
    <Box>
      {sections
        .slice()
        .sort((a, b) => a.order - b.order)
        .map((section) => (
          <Paper key={section.id} sx={{ p: 2, mb: 2 }} variant="outlined">
            <Typography variant="h6" gutterBottom>
              {section.title || `Article ${section.order}`}
            </Typography>
            <Divider sx={{ my: 1 }} />
            {(section.content ?? [])
              .slice()
              .sort((a, b) => a.order - b.order)
              .map((content) => (
                <Box key={content.id} sx={{ mb: 2 }}>
                  {content.content_type_id !== LIST_CONTENT_TYPE_ID &&
                    content.title && (
                      <Typography variant="subtitle2" fontWeight="bold">
                        {content.title}
                      </Typography>
                    )}
                  {content.paragraph && (
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {content.paragraph}
                    </Typography>
                  )}
                  {content.content_type_id === LIST_CONTENT_TYPE_ID &&
                    content.list?.length > 0 && (
                      <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                        {content.list
                          .slice()
                          .sort((a, b) => a.order - b.order)
                          .map((item) => (
                            <li key={item.id}>
                              <Typography variant="body2">
                                {item.content}
                              </Typography>
                            </li>
                          ))}
                      </Box>
                    )}
                </Box>
              ))}
          </Paper>
        ))}
    </Box>
  );
}

export default LCBylawsPage;
